import {
  useGetAccessibleLivePagesQuery,
  useGetFoldersQuery,
} from "@/shared/services";
import type { IStoriesFolder } from "@/shared/types";
import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store/analytics.store";
import { ANALYTICS_QUERY, type DataByDate } from "@pages/analytics/types";
import {
  buildFolderPathIndex,
  expandFolderMatchesToAncestors,
  formatFolderPath,
  orderFoldersByHierarchy,
} from "@pages/analytics/utils";
import {
  IconCheck,
  IconSearch,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";

export const FoldersFilter = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const { folder, setFolder } = useAnalyticsStoreShallow((s) => ({
    folder: s.folder,
    setFolder: s.setFolder,
  }));

  const { data: folders = [], isLoading: isLoadingFolders } =
    useGetFoldersQuery();

  const { data: accessibleLivePages = [], isLoading: isLoadingPages } =
    useGetAccessibleLivePagesQuery();
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });

  const { data: response, isLoading: isLoadingAnalytics } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.READERS_BY_DATE],
    enabled: isIntersecting,
  });

  const isLoading = isLoadingFolders || isLoadingPages || isLoadingAnalytics;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleFolderChange = useCallback(
    (f: IStoriesFolder) => {
      setFolder({
        id: f._id,
        name: f.name,
      });
      setIsOpen(false);
    },
    [setFolder],
  );

  const handleClear = useCallback(() => {
    setFolder({ id: "", name: "" });
    setIsOpen(false);
  }, [setFolder]);

  const readersByDate = response?.[ANALYTICS_QUERY.READERS_BY_DATE] as
    | DataByDate[]
    | undefined;

  // Resolved once for the whole list rather than per row: every row needs its
  // ancestor names, and search matches against the full path.
  const pathIndex = useMemo(() => buildFolderPathIndex(folders), [folders]);

  const filteredFolders = useMemo(() => {
    // First filter folders by those that contain stories with analytics data
    let availableFolders = folders;
    if (readersByDate && accessibleLivePages.length > 0) {
      // Get unique story IDs from analytics data
      const storyIdsWithAnalytics = new Set(
        readersByDate.map((s: DataByDate) => s.story_id),
      );

      // Get folder IDs that directly hold at least one story with analytics
      const folderIdsHoldingAnalytics = accessibleLivePages
        .filter((page) => storyIdsWithAnalytics.has(page._id))
        .map((page) => page.folderId)
        .filter(Boolean) as string[]; // Remove any undefined/null values

      // Stories only ever sit in the innermost folder, so matching those ids
      // exactly dropped every parent: a `Client Name > Job Name` structure
      // offered only `Job Name` and the client-level folder was unselectable.
      // A folder is worth offering when it, or anything nested under it, holds
      // a story with analytics.
      const folderIdsWithAnalytics = expandFolderMatchesToAncestors(
        folders,
        folderIdsHoldingAnalytics,
      );

      availableFolders = folders.filter((f: IStoriesFolder) =>
        folderIdsWithAnalytics.has(f._id),
      );
    }

    // Then filter by search term. Matching the folder's own name alone made
    // nesting decide what was findable — with parents now listed too, typing a
    // client's name has to reach the jobs underneath it, and typing a job's
    // name has to reach it however deep it sits.
    if (!debouncedSearchTerm.trim()) {
      return orderFoldersByHierarchy(availableFolders);
    }
    const search = debouncedSearchTerm.toLowerCase();
    const matches = availableFolders.filter((f: IStoriesFolder) => {
      const ancestors = pathIndex.get(f._id)?.ancestors ?? [];
      return [...ancestors, f.name].join(" / ").toLowerCase().includes(search);
    });
    // Ordered last so the walk only ever sees folders that survived the
    // filters — a match whose parent was filtered out reads as top level
    // rather than indenting under a row that is not there.
    return orderFoldersByHierarchy(matches);
  }, [
    folders,
    debouncedSearchTerm,
    readersByDate,
    accessibleLivePages,
    pathIndex,
  ]);

  const selectedFolderPath = useMemo(() => {
    if (!folder.id) return "";
    const ancestors = pathIndex.get(folder.id)?.ancestors ?? [];
    return [...ancestors, folder.name].join(" / ");
  }, [folder.id, folder.name, pathIndex]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(() => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
      }, 250);
    }
  }, []);

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-start"
      triggerWidth
    >
      <Popover.Trigger
        ref={ref}
        className={cn(
          "tw-flex tw-h-9 tw-cursor-pointer active:!tw-outline-offset-0 tw-max-w-40 active:!tw-outline-1 active:!tw-outline-primary tw-items-center tw-shadow-2xs tw-gap-1 tw-rounded-lg tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-px-2 tw-transition-colors !tw-outline-offset-0 tw-duration-100 hover:!tw-outline-primary -xl:tw-w-full -xl:tw-max-w-[700px]",
          {
            "!tw-outline-primary": isOpen || !!folder.id,
          },
        )}
      >
        <span
          // The trigger is too narrow for a path, but a bare leaf name cannot
          // say which `Q1` is selected — so the full path lives in the tooltip.
          title={selectedFolderPath || undefined}
          className="tw-block tw-px-1 tw-truncate tw-font-sans tw-text-sm tw-font-medium tw-text-blue"
        >
          {folder.name || "All folders"}
        </span>
        <IconSelector className="tw-size-4 tw-text-blue-500 tw-ml-auto" />
        {folder.id && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleClear(); } }}
            role="button"
            tabIndex={0}
            className="tw-ml-1 tw-flex tw-h-full tw-items-center tw-justify-center tw-border-l tw-border-solid tw-border-primary tw-pl-2"
          >
            <IconX className="tw-size-4 tw-text-primary" />
          </div>
        )}
      </Popover.Trigger>
      <Popover.Content className="tw-rounded-lg !tw-w-auto tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-shadow-md">
        <div className="tw-relative tw-h-9 tw-px-3 tw-border-b tw-border-b-solid tw-border-b-blue-100">
          <IconSearch className="tw-size-4 tw-text-blue-500 tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-3" />
          <input
            type="text"
            placeholder="Search folder"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tw-pl-6 tw-outline-none tw-w-full tw-h-full tw-rounded-t-lg tw-text-sm tw-font-normal tw-text-blue placeholder:tw-text-blue-500"
          />
        </div>
        <ScrollArea maxHeight={250}>
          <div className="tw-p-1 tw-space-y-0.5 tw-max-w-full tw-min-w-0">
            {isLoading ? (
              <FolderSkeleton />
            ) : filteredFolders.length === 0 ? (
              <div className="tw-py-3 tw-text-center tw-text-sm tw-text-blue-500">
                No folders available
              </div>
            ) : (
              filteredFolders.map(({ folder: f, depth }) => {
                const { ancestors = [], hasChildren = false } =
                  pathIndex.get(f._id) ?? {};
                const ancestorPath = formatFolderPath(ancestors);
                // Selecting a folder now returns its whole subtree, which the
                // row has to say out loud — otherwise a parent's totals look
                // like they should add up alongside its children's.
                const context = [
                  ancestorPath && `in ${ancestorPath}`,
                  hasChildren && "includes subfolders",
                ]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <button
                    type="button"
                    key={f._id}
                    onClick={() => handleFolderChange(f)}
                    title={[...ancestors, f.name].join(" / ")}
                    // Depth is unbounded, so the inset is computed rather than
                    // a class; depth 0 keeps the row's own tw-px-2.
                    style={{ paddingLeft: `${0.5 + depth * 0.75}rem` }}
                    className={cn(
                      "tw-flex tw-min-h-9 tw-cursor-pointer tw-w-72 tw-items-center tw-justify-between tw-gap-3 tw-rounded tw-px-2 tw-py-1.5 tw-font-sans tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                      {
                        "tw-bg-blue-100 hover:tw-bg-blue-100":
                          folder.id === f._id,
                      },
                    )}
                  >
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-text-left">
                      <span className="tw-truncate tw-whitespace-nowrap">
                        {f.name}
                      </span>
                      {context && (
                        <span className="tw-truncate tw-whitespace-nowrap tw-text-xs tw-font-normal tw-text-blue-500">
                          {context}
                        </span>
                      )}
                    </span>
                    {folder.id === f._id && (
                      <IconCheck className="tw-size-4 tw-text-blue tw-flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>
      </Popover.Content>
    </Popover>
  );
});

const FOLDER_SKELETON_WIDTHS = [87, 45, 102, 63, 29];

const FolderSkeleton = () => {
  const widthArray = FOLDER_SKELETON_WIDTHS;
  return (
    <>
      {widthArray.map((width) => (
        <div key={width} className="tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading">
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};
